import { state, set, patch, subscribe } from './state/store.js';
import { getCart, getRecommendations, applyDiscountCode, CartLoadError } from './lib/shopify.js';
import { validateFields } from './lib/validate.js';
import { formatPhoneE164 } from './lib/format.js';
import { TAB_FIELDS } from './config/tabs.js';
import { DELIVERY_METHODS } from './config/delivery.js';
import { MINIMUM_ORDER_VALUE } from './config/settings.js';
import { formatPrice } from './lib/format.js';

import { initContact } from './sections/contact/contact.js';
import { initDelivery } from './sections/delivery/delivery.js';
import { initPayment, getActiveProvider as getPaymentProvider } from './sections/payment/payment.js';
import { initSummary } from './sections/summary/summary.js';
import { isUnavailable } from './sections/summary/cart.js';
import { initPixel, track as pixelTrack, contentsFromCartLines } from './lib/pixel.js';
import { registerRetryCart } from './state/actions.js';
import { initGtag, gtagEvent, itemsFromCartLines, parseGaClientId } from './lib/gtag.js';
import { PAYMENT_METHODS } from './config/payment.js';

// ---------------------------------------------------------------------------
// Reload-survival: snapshot shop params + form state in sessionStorage keyed
// by the access token. On the first visit we parse the URL params and write
// a snapshot; on every subsequent reload we hydrate from that snapshot so
// the cart, contact fields, delivery selection, and payment method all
// survive a refresh. sessionStorage is per-tab so concurrent checkouts in
// different tabs don't collide. Cleared on successful order submit.
// ---------------------------------------------------------------------------

const SNAPSHOT_KEY = (token) => `wmcheckout:${token}`;

// Resolves to whether a Meta pixel is active for this shop. Set in init();
// hydrateCart() awaits it before firing InitiateCheckout so the event isn't
// dropped if the cart loads before /api/pixel-config returns.
let pixelReady = Promise.resolve(false);

// Resolves to whether GA4 (the Google tag) is active for this shop. Set in
// init(); the funnel events await it so they aren't dropped when the cart /
// a selection resolves before /api/pixel-config returns.
let ga4Ready = Promise.resolve(false);

/** Current cart value for funnel events (post-discount total, falls back to subtotal). */
function cartValue() {
  return state.cart?.total || state.cart?.subtotal || 0;
}

function saveSnapshot() {
  const token = state.shop?.sessionToken;
  if (!token) return;
  try {
    sessionStorage.setItem(SNAPSHOT_KEY(token), JSON.stringify({
      v: 1,
      ts: Date.now(),
      shop:     state.shop,
      contact:  state.contact,
      delivery: state.delivery,
      payment:  state.payment ? { methodId: state.payment.methodId } : {},
      tab:      state.tab,
      promo:    state.promo,
    }));
  } catch (_) { /* storage disabled or full — degrade silently */ }
}

function loadSnapshot(token) {
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY(token));
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function clearSnapshot() {
  const token = state.shop?.sessionToken;
  if (!token) return;
  try { sessionStorage.removeItem(SNAPSHOT_KEY(token)); } catch (_) {}
}

/**
 * Update both top-bar links to point back at the customer's source store:
 *   #brand     → https://<storeName>           (logo click)
 *   #cart-link → https://<storeName>/cart      (cart icon click)
 * No-op when storeName is missing (e.g. customer arrived without chekly).
 */
function applyTopbarLinks(storeName) {
  if (!storeName) return;
  const raw = String(storeName).trim();
  const origin = (/^https?:\/\//.test(raw) ? raw : `https://${raw}`).replace(/\/+$/, '');
  const brand = document.getElementById('brand');
  if (brand) brand.href = origin;
  const cartLink = document.getElementById('cart-link');
  if (cartLink) cartLink.href = `${origin}/cart`;
}

/**
 * Generate an unguessable 16-char base64url session token (96 bits of entropy).
 * Reused as the access token gating the thank-you page and the Plata resume
 * endpoint for this order. Persisted to the Shopify order as a customAttribute
 * so the merchant can rebuild the link from Shopify admin later.
 */
function generateSessionToken() {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Read the URL params chekly-script appends when redirecting from the
 * Shopify store, and hydrate `state.shop` + `state.contact` accordingly.
 * Then strip the storefront query params from the visible URL and replace
 * them with `?t=<sessionToken>` — the token rides forward to order creation
 * and into the thank-you / resume-plata URLs.
 */
/**
 * True when this page load is a fresh chekly redirect (not a reload from
 * sessionStorage). Used by hydrateCart() to drop any discount codes that
 * were left over from a previous session — the promo input is single-use
 * per checkout session.
 */
let isFreshArrival = false;

function readShopParams() {
  const q = new URLSearchParams(location.search);
  const tokenInUrl = q.get('t');

  // Reload case: the URL was cleaned on the first visit, so it only has
  // `?t=<token>` — no cartToken / shopifyDomain. Try to rebuild the whole
  // session from sessionStorage. If we have a snapshot, we're done; if not,
  // fall through to the URL parser (probably ends in the demo state).
  if (tokenInUrl && !q.get('cartToken')) {
    const snap = loadSnapshot(tokenInUrl);
    if (snap?.shop?.cartToken) {
      set({
        shop:     snap.shop,
        contact:  snap.contact  || {},
        delivery: snap.delivery || {},
        payment:  snap.payment  || {},
        tab:      snap.tab      || 'self',
        promo:    snap.promo    || {},
      });
      // URL is already in the clean form, no replaceState needed.
      // Brand + cart link hrefs still need updating.
      applyTopbarLinks(snap.shop?.storeName);
      return;
    }
  }

  const prefill = {
    firstName: q.get('user_name')     || '',
    lastName:  q.get('user_lastname') || '',
    email:     q.get('user_email')    || '',
    phone:     q.get('user_phone')    || '',
  };
  const tracking = {};
  // Standard UTMs + click IDs (fbclid, ttclid, epik, sccid, gclid, wbraid,
  // gbraid) + the pixel cookies chekly-script forwards as query params
  // (_fbp/_fbc for Meta CAPI, ttp for TikTok, _ga/_gcl_au for Google).
  for (const k of [
    'utm_source','utm_medium','utm_campaign','utm_content','utm_term','utm_lang',
    'fbclid','ttclid','epik','sccid','gclid','wbraid','gbraid',
    '_fbp','_fbc','ttp','_ga','_gcl_au','_gcl_aw',
  ]) {
    const v = q.get(k);
    if (v) tracking[k] = v;
  }
  // Reuse the token already in the URL if the customer reloaded, so the
  // session stays stable across page refreshes.
  const sessionToken = q.get('t') || generateSessionToken();
  set({
    shop: {
      storeName:     q.get('storeName')     || null,
      cartToken:     q.get('cartToken')     || null,
      curRate:       Number(q.get('curRate') || 1),
      theme:         q.get('theme')         || null,
      shopifyDomain: q.get('shopifyDomain') || null,
      sessionToken,
      prefill,
      tracking,
    },
    contact: { ...prefill },
  });

  // Strip the storefront params from the URL but keep the access token.
  const clean = `${location.pathname.replace(/\/+$/, '') || '/checkout'}?t=${encodeURIComponent(sessionToken)}`;
  history.replaceState(null, '', clean);

  // Point the topbar logo + cart icon back to the store the customer came from.
  applyTopbarLinks(q.get('storeName'));

  // First snapshot — captures the URL-derived state so any reload from
  // here on restores cleanly. Subsequent state edits are saved by
  // setupSnapshotPersistence().
  saveSnapshot();

  // Flag this as a fresh chekly arrival so hydrateCart() can drop any
  // discount codes the previous session left on the Shopify cart.
  isFreshArrival = true;
}

/**
 * Load the customer's cart. On failure the checkout shows an explicit error
 * with a retry instead of silently rendering demo data — see getCart().
 * Exported via retryHydrateCart() so the error panel can re-run it in place;
 * we must NOT ask the customer to reload, because readShopParams() has already
 * stripped cartToken from the URL and a reload with sessionStorage unavailable
 * would permanently demote the session to the demo cart.
 */
async function retryHydrateCart() {
  patch('ui', { cartError: { ...(state.ui?.cartError || {}), retrying: true } });
  await hydrateCart();
}

async function hydrateCart() {
  const { shopifyDomain, cartToken } = state.shop;
  let cart;
  try {
    cart = await getCart({ shop: shopifyDomain, cartToken });
  } catch (err) {
    if (!(err instanceof CartLoadError)) throw err;
    console.error('[checkout] cart load failed', err.name, err.status, err.message);
    patch('ui', {
      cartError: {
        permanent: !!err.permanent,
        status: err.status,
        retrying: false,
      },
    });
    return;
  }
  // Cleared only on success, so a retry that fails again keeps the panel up.
  if (state.ui?.cartError) patch('ui', { cartError: null });

  // Single-use promo per checkout session: if the customer arrived fresh
  // from chekly AND the Shopify cart still carries discount codes from a
  // previous visit, drop them. Reloads (snapshot-restored sessions) skip
  // this so a mid-checkout refresh doesn't wipe the customer's progress.
  if (isFreshArrival && cart?.id && (cart.discountCodes || []).length) {
    isFreshArrival = false;
    try {
      const cleared = await applyDiscountCode({ shop: shopifyDomain, cartId: cart.id, code: '' });
      if (cleared?.cart) cart = cleared.cart;
    } catch (err) {
      // Non-fatal — the customer can still re-apply or remove manually.
      console.warn('[checkout] failed to clear stale discount on fresh arrival:', err);
    }
    // Also clear any local promo state the snapshot may have restored.
    set({ promo: null });
  } else {
    isFreshArrival = false;
  }

  set({ cart });

  // Meta browser pixel: the customer has reached our checkout with a live
  // cart → InitiateCheckout. Awaits the pixel init so this isn't dropped when
  // the cart resolves first; no-op when the shop has no configured pixel.
  pixelReady.then((active) => {
    if (!active) return;
    pixelTrack('InitiateCheckout', {
      value:    cart.total || cart.subtotal || 0,
      currency: cart.currency || 'UAH',
      ...contentsFromCartLines(cart.lines || []),
    });
  });

  // GA4 begin_checkout — the Google equivalent of the Meta InitiateCheckout
  // above. This event was effectively dead in GA4 (the storefront's tag never
  // ran on our off-Shopify checkout); firing it here feeds the funnel + Ads
  // remarketing audiences. No-op when GA4 isn't configured for the shop.
  ga4Ready.then((active) => {
    if (!active) return;
    gtagEvent('begin_checkout', {
      currency: cart.currency || 'UAH',
      value:    cart.total || cart.subtotal || 0,
      items:    itemsFromCartLines(cart.lines || []),
    });
  });

  // Recommendations: keyed off the first line's product when we have one.
  const seedProduct = cart.lines?.[0]?.productId;
  const upsells = await getRecommendations({ shop: shopifyDomain, productId: seedProduct });
  set({ upsells });
}

function wirePayButton() {
  const btn = document.getElementById('pay-btn');
  if (!btn) return;

  btn.addEventListener('click', async () => {
    // 0) Hard floor: refuse anything below the minimum order value.
    //    The button is already disabled at this point, but guarding here
    //    too means a programmatic click can't slip through.
    // Only payable lines count: a sold-out line is excluded from the totals
    // the customer sees (sections/summary/totals.js), so including it here
    // would let a cart clear the floor on money that will never be charged.
    const grandTotal = (state.cart?.lines || [])
      .filter((it) => !isUnavailable(it))
      .reduce((a, it) => a + it.price * it.qty, 0)
      - (typeof state.cart?.discount === 'number' ? state.cart.discount : 0);
    if (MINIMUM_ORDER_VALUE > 0 && grandTotal > 0 && grandTotal < MINIMUM_ORDER_VALUE) {
      btn.textContent = `Мінімум ${formatPrice(MINIMUM_ORDER_VALUE)}`;
      setTimeout(() => { btn.textContent = 'Сплатити'; }, 2200);
      return;
    }

    // 1) Contact fields (per tab).
    const fields = TAB_FIELDS[state.tab] || [];
    const contactErrors = validateFields(fields, state.contact, state.delivery.methodId);

    // 2) Delivery — load the carrier provider and let it validate (city/branch/
    //    street/building + patronymic-for-UP). Aggregate with contact errors.
    let deliveryErrors = {};
    const carrier = DELIVERY_METHODS.find((m) => m.id === state.delivery.methodId);
    if (carrier?.provider) {
      try {
        const mod = await carrier.provider();
        deliveryErrors = mod.validate?.(state) || {};
      } catch (e) {
        console.warn('[pay] delivery validation skipped', e);
      }
    }

    const errors = { ...contactErrors, ...deliveryErrors };
    patch('ui', { errors });

    if (Object.keys(errors).length > 0) {
      // Focus the first contact field that errored; for delivery errors we
      // just surface the messages via the UI later.
      const firstContact = Object.keys(contactErrors)[0];
      if (firstContact) {
        const el = document.querySelector(`#contact-fields [name="${firstContact}"]`);
        el?.focus({ preventScroll: false });
      } else {
        const firstDelivery = Object.keys(deliveryErrors)[0];
        const map = { city: 'dlv-city-input', warehouse: 'dlv-warehouse-input',
                      street: 'dlv-street-input', building: 'dlv-building-input',
                      patronymic: 'dlv-patronymic-input' };
        const el = document.getElementById(map[firstDelivery]);
        el?.focus({ preventScroll: false });
      }
      btn.textContent = 'Перевірте форму';
      setTimeout(() => { btn.textContent = 'Сплатити'; }, 2200);
      return;
    }

    // GA4 add_payment_info — fired once the form is valid and the customer is
    // committing to pay, with the selected method as payment_type. This is the
    // last funnel step before purchase; it's dead in GA4 otherwise (no native
    // payment step on our checkout). No-op when GA4 isn't configured.
    ga4Ready.then((active) => {
      if (!active) return;
      const m = PAYMENT_METHODS.find((p) => p.id === state.payment?.methodId);
      gtagEvent('add_payment_info', {
        currency:     state.cart?.currency || 'UAH',
        value:        cartValue(),
        payment_type: m?.label || state.payment?.methodId || '',
        items:        itemsFromCartLines(state.cart?.lines || []),
      });
    });

    patch('ui', { submitting: true });
    btn.disabled = true;
    btn.textContent = 'Обробка...';

    try {
      const provider = await getPaymentProvider();
      if (!provider?.init) throw new Error('Метод оплати не налаштовано');

      // Convert phone fields to E.164 (Shopify rejects local forms like
      // "682162166" — needs "+380682162166"). The country comes from each
      // field's matching <field>Country slot in state.contact.
      const contact = { ...state.contact };
      if (contact.phone)    contact.phone    = formatPhoneE164(contact.phone,    contact.phoneCountry);
      if (contact.rcvPhone) contact.rcvPhone = formatPhoneE164(contact.rcvPhone, contact.rcvPhoneCountry);

      const order = {
        tab: state.tab,
        contact,
        delivery: state.delivery,
        payment: { method: state.payment.methodId, methodId: state.payment.methodId },
        cart: state.cart,
        promo: state.promo,
        shop: state.shop,
        comment: document.getElementById('comment')?.value?.trim() || '',
        noCallRequested: !!document.getElementById('opt-no-call')?.checked,
        // The marketing checkbox has been on the form since launch, pre-ticked,
        // but nothing ever read it — so every customer landed in Shopify as
        // NOT_SUBSCRIBED regardless of what they chose. Send the answer.
        emailConsent:    !!document.getElementById('opt-promos')?.checked,
      };

      const result = await provider.init(order);
      // Order accepted server-side — drop the snapshot so the customer
      // doesn't see stale form state if they bounce back from Plata
      // or refresh the checkout page.
      clearSnapshot();
      if (result?.redirectUrl) {
        location.href = result.redirectUrl;
        return;
      }
      // COD path: server returned an order name → land the customer on the
      // thank-you page for that order.
      if (result?.nextUrl) {
        location.href = result.nextUrl;
        return;
      }
      if (result?.orderName) {
        location.href = `/checkout/thank-you/${encodeURIComponent(result.orderName)}`;
        return;
      }
      btn.textContent = '✓ Замовлення оформлено';
    } catch (err) {
      console.error('Order failed', err);
      // If the server hinted at the failing form field, surface the same
      // message inline next to it. For the "other" tab, Shopify validates
      // the receiver's data (rcvPhone / rcvFirstName / rcvLastName) — map
      // the field name accordingly so the right input lights up.
      if (err.field) {
        const otherTabMap = { phone: 'rcvPhone', firstName: 'rcvFirstName', lastName: 'rcvLastName' };
        const targetField = (state.tab === 'other' && otherTabMap[err.field]) || err.field;
        patch('ui', { errors: { ...(state.ui?.errors || {}), [targetField]: err.message } });
      }
      // Pay-button error display: if the server already gave us a UA
      // message it stays; raw English (network failures, etc.) gets
      // replaced with a generic UA fallback.
      const rawErr = String(err.message || err).trim();
      const shownErr = /^[А-ЯҐЄІЇа-яґєії]/.test(rawErr)
        ? rawErr
        : 'Сталася помилка. Спробуйте ще раз.';
      btn.textContent = `Помилка: ${shownErr.slice(0, 60)}`;
      setTimeout(() => {
        btn.textContent = 'Сплатити';
        btn.disabled = false;
        patch('ui', { submitting: false });
      }, 3500);
    }
  });
}

/**
 * GA4 add_shipping_info — fired when the customer has actually picked a
 * shipping destination (a branch/postomat or a delivery address), not merely
 * the default carrier. Delivery state patches on every keystroke (city/street
 * search), so we dedup on the concrete destination and only fire when one is
 * set. Re-fires if they change carrier or destination. No-op when GA4 isn't
 * configured for the shop.
 */
function setupFunnelEvents() {
  let lastShip = '';
  subscribe(['delivery'], () => {
    const d = state.delivery || {};
    const destination = d.warehouseRef || d.streetRef || d.postcode || '';
    if (!d.methodId || !destination) return;
    const key = `${d.methodId}|${d.subOptionId}|${destination}|${d.building || ''}`;
    if (key === lastShip) return;
    lastShip = key;
    ga4Ready.then((active) => {
      if (!active) return;
      const method = DELIVERY_METHODS.find((m) => m.id === d.methodId);
      const sub = method?.subOptions?.find((s) => s.id === d.subOptionId);
      gtagEvent('add_shipping_info', {
        currency:      state.cart?.currency || 'UAH',
        value:         cartValue(),
        shipping_tier: [method?.name, sub?.label].filter(Boolean).join(' — '),
        items:         itemsFromCartLines(state.cart?.lines || []),
      });
    });
  });
}

function setupSnapshotPersistence() {
  let timer = null;
  const debouncedSave = () => {
    clearTimeout(timer);
    timer = setTimeout(saveSnapshot, 400);
  };
  // Persist subsequent edits to shop / form sections so a reload at any
  // point in the checkout flow restores cleanly. Cart + upsells skip
  // intentionally — they're re-fetched on boot from the cart token.
  subscribe(['shop', 'contact', 'delivery', 'payment', 'tab', 'promo'], debouncedSave);
}

/**
 * Compact-mobile preview flag — gated by ?preview=1 in the URL or a
 * previously-stored flag in sessionStorage. Set on <body> as early as
 * possible so the gated CSS rules apply before the first paint.
 */
function applyPreviewFlag() {
  const SS_KEY = 'wmcheckout:previewCompact';
  const fromUrl = new URLSearchParams(location.search).get('preview') === '1';
  let active = fromUrl;
  if (!active) {
    try { active = sessionStorage.getItem(SS_KEY) === '1'; } catch (_) {}
  }
  if (active) {
    document.body.classList.add('compact');
    try { sessionStorage.setItem(SS_KEY, '1'); } catch (_) {}
  }
}

/**
 * Move the "Деталі замовлення" card between the form column (mobile) and
 * the summary column (desktop) based on the 980 px breakpoint. DOM stays
 * as a single source of truth — appendChild moves the node — so all the
 * existing event handlers and ID lookups keep working.
 */
function setupTotalsCardPosition() {
  const card    = document.getElementById('totals-card');
  const mobile  = document.getElementById('totals-anchor-mobile');
  const desktop = document.getElementById('totals-anchor-desktop');
  if (!card || !mobile || !desktop) return;

  const mq = matchMedia('(min-width: 981px)');
  function reposition() {
    const target = mq.matches ? desktop : mobile;
    if (card.parentNode !== target) target.appendChild(card);
  }
  // Older browsers expose addListener; use it as a fallback.
  if (mq.addEventListener) mq.addEventListener('change', reposition);
  else if (mq.addListener) mq.addListener(reposition);
  reposition();
}

function init() {
  applyPreviewFlag();
  readShopParams();
  // Kick off the Meta pixel as early as we know the shop (fires PageView).
  pixelReady = initPixel(state.shop?.shopifyDomain);
  // Kick off the Google tag (GA4, fires page_view). client_id is stitched from
  // the forwarded _ga so checkout events join the storefront user's GA4
  // audiences — the basis for Google Ads remarketing / retargeting lists.
  ga4Ready = initGtag(state.shop?.shopifyDomain, {
    clientId: parseGaClientId(state.shop?.tracking?._ga),
  });
  initContact();
  initDelivery();
  initPayment();
  initSummary();
  wirePayButton();
  setupFunnelEvents();
  setupSnapshotPersistence();
  setupTotalsCardPosition();
  // Let the cart section's error panel re-run hydration without importing
  // this module (see state/actions.js).
  registerRetryCart(retryHydrateCart);

  // Async: load cart + upsells. UI starts rendered with fallback data immediately.
  hydrateCart();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
